import GhHtmlElement from '@gudhub/gh-html-element';
import html from './emailInbox.html';
import { emailDataFetchMixin } from './mixins/emailDataFetchMixin.js';
import { emailRenderingMixin } from './mixins/emailRenderingMixin.js';
import { emailComposerMixin } from './mixins/emailComposerMixin.js';
import './emailInbox.scss';

class GhEmailInbox extends GhHtmlElement {

    //********************* CONNECTED CALLBACK ********************************//
    // <gh-email-inbox> is a global, attribute-less element (like <gh-call-profile>/
    // <gh-calls>) - the base class's attributeChangedCallback never calls
    // onInit(), so call it directly here instead.
    connectedCallback() {
        if(this._emailInboxInitialized) return;
        this._emailInboxInitialized = true;

        this.waitForActiveUser().then(() => this.onInit()).catch(err => {
            console.error('GhEmailInbox init failed', err);
        });
    }

    //********************* WAIT FOR ACTIVE USER ********************************//
    waitForActiveUser() {
        if(window.gudhub?.storage?.user?.user_id) return Promise.resolve();

        return new Promise(resolve => {
            const interval = setInterval(() => {
                if(window.gudhub?.storage?.user?.user_id) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);
        });
    }

    async onInit() {
        this.activeUserId = String(gudhub.storage.user.user_id);
        this.viewMode = 'list'; // 'list' | 'thread' | 'compose'
        this.accounts = [];
        this.activeAccountId = null;
        this.threads = [];
        this.threadMessages = [];
        this.activeThreadKey = null;
        this.searchQuery = '';
        this.composeState = null;
        this.loading = true;

        this.accounts = (await this.fetchAccounts()).map(a => this.normalizeAccount(a));
        this.activeAccountId = this.accounts[0]?.account_id || null;
        this.threads = this.activeAccountId ? (await this.fetchInbox()).map(t => this.normalizeThread(t)) : [];
        this.loading = false;

        this.render();

        this._realtimeHandler = () => {
            clearTimeout(this._realtimeDebounce);
            this._realtimeDebounce = setTimeout(() => this.refresh(), 300);
        };
        gudhub.on('gudhub_email', { user_id: this.activeUserId }, this._realtimeHandler);
    }

    onDestroy() {
        gudhub.destroy('gudhub_email', { user_id: this.activeUserId }, this._realtimeHandler);
        clearTimeout(this._realtimeDebounce);
    }

    //********************* RENDER ********************************//
    // Re-renders the current viewMode's content and re-wires events. Called
    // after every state change since GhHtmlElement.render() replaces innerHTML
    // wholesale (no diffing).
    render() {
        super.render(html);
        this.populateFormFields();
        this.initEditor();
        this.wireEvents();
        this.wireIframeResize();
    }

    //********************* POPULATE FORM FIELDS ********************************//
    // Sets input .value via JS rather than the `value="${...}"` HTML attribute
    // for every user-editable/user-derived field (search box, compose To/Cc/
    // Subject/body) - the template can only build markup from bare identifiers
    // (see GhHtmlElement.render's `new Function`), so it has no way to
    // HTML-attribute-escape these safely; assigning `.value` in JS never
    // parses its argument as markup, sidestepping the issue entirely.
    populateFormFields() {
        const searchInput = this.querySelector('.email_inbox__search_input');
        if(searchInput) searchInput.value = this.searchQuery;

        if(this.viewMode === 'compose' && this.composeState) {
            const to = this.querySelector('.email_compose__to');
            const cc = this.querySelector('.email_compose__cc');
            const subject = this.querySelector('.email_compose__subject');

            if(to) to.value = this.composeState.to;
            if(cc) cc.value = this.composeState.cc;
            if(subject) subject.value = this.composeState.subject;
        }
    }

    //********************* NORMALIZE ACCOUNT ********************************//
    // * account (Object) — raw account from GET /email/accounts (secrets already stripped server-side)
    normalizeAccount(account) {
        return {
            ...account,
            displayLabel: this.escapeHtml(account.display_name || account.email_address)
        };
    }

    //********************* REFRESH ********************************//
    // Called by gh_conversation.js each time the email view is made visible,
    // and on real-time push. Only reloads when resting on the thread list -
    // an in-progress reply/compose is never silently discarded.
    async refresh() {
        this.accounts = (await this.fetchAccounts()).map(a => this.normalizeAccount(a));
        if(!this.activeAccountId) this.activeAccountId = this.accounts[0]?.account_id || null;

        if(this.viewMode === 'list' && this.activeAccountId) {
            this.threads = (await this.fetchInbox({ q: this.searchQuery })).map(t => this.normalizeThread(t));
        }

        this.render();
    }

    //********************* NORMALIZE THREAD ********************************//
    // Precomputes every escaped/formatted value the template needs - the
    // template itself can only reference bare instance-property identifiers
    // (see GhHtmlElement.render's `new Function`), it cannot call `this.*`
    // helper methods, so all mixin calls happen here instead.
    // * thread (Object) — raw thread summary from GET /email/inbox
    normalizeThread(thread) {
        return {
            thread_key: thread.thread_key,
            subject: this.escapeHtml(thread.subject || '(no subject)'),
            fromName: this.formatFrom(thread.from),
            snippet: this.escapeHtml(thread.snippet || ''),
            timeAgo: this.getTimeAgo(thread.received_at),
            unread: (thread.unseen_count || 0) > 0,
            message_count: thread.message_count || 1
        };
    }

    //********************* NORMALIZE MESSAGE ********************************//
    // * message (Object) — raw message from GET /email/thread/:thread_key
    normalizeMessage(message) {
        return {
            message_id: message.message_id,
            direction: message.direction,
            fromName: this.formatFrom(message.from),
            fromAddress: this.escapeHtml(message.from?.address || ''),
            toList: this.formatAddressList(message.to),
            ccList: this.formatAddressList(message.cc),
            timeAgo: this.getTimeAgo(message.received_at),
            srcdoc: this.escapeHtml(this.buildMessageBodySrcdoc(message)),
            attachmentsHtml: (message.attachments || []).map(a => this.renderAttachmentCard(a)).join(''),
            rfc822_message_id: message.rfc822_message_id,
            references: message.references || [],
            fromRaw: message.from,
            subjectRaw: message.subject || ''
        };
    }

    //********************* WIRE EVENTS ********************************//
    // Single delegated click listener (data-action) plus the handful of
    // non-click listeners (search input, account select, compose form,
    // attachment file input) that need direct binding. Re-called after every
    // render() so handlers always refer to the latest DOM.
    wireEvents() {
        if(this._clickHandler) this.removeEventListener('click', this._clickHandler);

        this._clickHandler = (event) => {
            const actionEl = event.target.closest('[data-action]');
            if(!actionEl) return;

            const action = actionEl.dataset.action;

            if(action === 'back') return this.navigateBack();
            if(action === 'compose') return this.openCompose();
            if(action === 'reply') return this.openReply();
            if(action === 'send') return this.sendCompose();
            if(action === 'cancel-compose') return this.viewMode === 'thread' ? this.showThread(this.activeThreadKey) : this.showList();
            if(action === 'remove-attachment') return this.removeComposeAttachment(Number(actionEl.dataset.index));

            if(action === 'open-thread') {
                event.preventDefault();
                this.openThread(actionEl.dataset.threadKey);
            }
        };

        this.addEventListener('click', this._clickHandler);

        const accountSelect = this.querySelector('.email_inbox__account_select');
        if(accountSelect) {
            accountSelect.onchange = () => this.selectAccount(accountSelect.value);
        }

        const searchInput = this.querySelector('.email_inbox__search_input');
        if(searchInput) {
            searchInput.oninput = () => {
                clearTimeout(this._searchDebounce);
                this._searchDebounce = setTimeout(() => this.search(searchInput.value), 300);
            };
        }

        const attachmentInput = this.querySelector('.email_compose__attachment_input');
        if(attachmentInput) {
            attachmentInput.onchange = () => {
                const file = attachmentInput.files[0];
                if(file) this.addComposeAttachment(file);
                attachmentInput.value = '';
            };
        }

        const composeForm = this.querySelector('.email_compose__form');
        if(composeForm) {
            composeForm.onsubmit = (event) => {
                event.preventDefault();
                this.sendCompose();
            };
        }
    }

    //********************* WIRE IFRAME RESIZE ********************************//
    // Message bodies render inside a sandboxed srcdoc iframe (see
    // buildMessageBodySrcdoc) - auto-grows it to the content's actual height
    // once loaded, since a fixed height would either clip long emails or
    // leave huge blank space under short ones.
    wireIframeResize() {
        this.querySelectorAll('.email_message__frame').forEach(frame => {
            frame.addEventListener('load', () => {
                try {
                    frame.style.height = `${frame.contentDocument.documentElement.scrollHeight}px`;
                } catch(err) {
                    // Cross-origin-like restriction if the sandbox ever tightens - leave the CSS min-height fallback.
                }
            });
        });
    }

    //********************* SELECT ACCOUNT ********************************//
    async selectAccount(accountId) {
        this.activeAccountId = accountId;
        this.showList();
    }

    //********************* SEARCH ********************************//
    async search(query) {
        this.searchQuery = query;
        this.threads = (await this.fetchInbox({ q: this.searchQuery })).map(t => this.normalizeThread(t));
        this.render();

        // render() rebuilds the whole subtree, so the search input loses focus -
        // restore it (cursor at the end) since the user is likely still typing.
        const searchInput = this.querySelector('.email_inbox__search_input');
        if(searchInput) {
            searchInput.focus();
            searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
        }
    }

    //********************* SHOW LIST ********************************//
    async showList() {
        this.viewMode = 'list';
        this.activeThreadKey = null;
        this.composeState = null;
        this.threads = this.activeAccountId ? (await this.fetchInbox({ q: this.searchQuery })).map(t => this.normalizeThread(t)) : [];
        this.render();
    }

    //********************* OPEN THREAD ********************************//
    // * threadKey (string)
    async openThread(threadKey) {
        this.viewMode = 'thread';
        this.activeThreadKey = threadKey;

        const raw = await this.fetchThread(threadKey);
        this.threadMessages = raw.map(m => this.normalizeMessage(m));
        this.render();

        const lastInbound = [...raw].reverse().find(m => m.direction === 'inbound');
        if(lastInbound) this.markMessageRead(lastInbound.message_id);
    }

    //********************* SHOW THREAD ********************************//
    // Re-shows an already-loaded thread without refetching - used when
    // cancelling out of a reply compose.
    // * threadKey (string)
    showThread(threadKey) {
        this.viewMode = 'thread';
        this.activeThreadKey = threadKey;
        this.composeState = null;
        this.render();
    }

    //********************* OPEN COMPOSE ********************************//
    // New message - no thread context.
    openCompose() {
        this.viewMode = 'compose';
        this.composeState = { to: '', cc: '', subject: '', bodyHtml: '', attachments: [], inReplyTo: null, references: [], sending: false, error: null };
        this.render();
    }

    //********************* OPEN REPLY ********************************//
    // Reply to the last inbound message in the currently open thread -
    // pre-fills To/Subject and the RFC822 threading headers.
    openReply() {
        const last = [...this.threadMessages].reverse().find(m => m.direction === 'inbound') || this.threadMessages[this.threadMessages.length - 1];
        if(!last) return this.openCompose();

        const rawSubject = last.subjectRaw.trim();
        const subject = /^re:/i.test(rawSubject) ? rawSubject : `Re: ${rawSubject}`;

        this.viewMode = 'compose';
        this.composeState = {
            to: last.fromRaw?.address || '',
            cc: '',
            subject,
            bodyHtml: '',
            attachments: [],
            inReplyTo: last.rfc822_message_id,
            references: [...(last.references || []), last.rfc822_message_id].filter(Boolean),
            sending: false,
            error: null
        };
        this.render();
    }

    //********************* SNAPSHOT COMPOSE FORM ********************************//
    // Copies the live To/Cc/Subject inputs and editor HTML into composeState -
    // render() replaces innerHTML wholesale (see render()'s comment), so
    // anything the user typed must be captured here *before* calling render()
    // for any reason (attaching/removing a file, sending), or it's lost when
    // populateFormFields()/initEditor() rebuild the form from stale composeState.
    snapshotComposeForm() {
        if(!this.composeState) return;

        this.composeState.to = this.querySelector('.email_compose__to')?.value.trim() ?? this.composeState.to;
        this.composeState.cc = this.querySelector('.email_compose__cc')?.value.trim() ?? this.composeState.cc;
        this.composeState.subject = this.querySelector('.email_compose__subject')?.value.trim() ?? this.composeState.subject;
        this.composeState.bodyHtml = this.tuiEditor ? this.tuiEditor.getHTML() : this.composeState.bodyHtml;
    }

    //********************* ADD COMPOSE ATTACHMENT ********************************//
    // * file (File)
    async addComposeAttachment(file) {
        const uploaded = await this.uploadAttachment(file);
        this.snapshotComposeForm();
        this.composeState.attachments.push({ filename: uploaded.filename, name: uploaded.original_name, size: uploaded.size, mime_type: uploaded.mime_type });
        this.render();
    }

    //********************* REMOVE COMPOSE ATTACHMENT ********************************//
    // * index (number)
    removeComposeAttachment(index) {
        this.snapshotComposeForm();
        this.composeState.attachments.splice(index, 1);
        this.render();
    }

    //********************* SEND COMPOSE ********************************//
    async sendCompose() {
        // Written back immediately so a validation/send error re-render
        // (below) restores the draft instead of silently wiping it - see
        // populateFormFields() and emailComposerMixin's initEditor().
        this.snapshotComposeForm();
        const { to, cc, subject, bodyHtml } = this.composeState;

        if(!to || !subject) {
            this.composeState.error = 'To and Subject are required';
            this.render();
            return;
        }

        this.composeState.sending = true;
        this.composeState.error = null;
        this.render();

        try {
            await this.sendEmail({
                to, cc, subject,
                html: bodyHtml,
                attachments: this.composeState.attachments,
                inReplyTo: this.composeState.inReplyTo,
                references: this.composeState.references
            });

            if(this.activeThreadKey) {
                await this.openThread(this.activeThreadKey);
            } else {
                await this.showList();
            }
        } catch(err) {
            this.composeState.sending = false;
            this.composeState.error = err.message || 'Failed to send email';
            this.render();
        }
    }

    //********************* NAVIGATE BACK ********************************//
    navigateBack() {
        if(this.viewMode === 'compose') {
            return this.activeThreadKey ? this.showThread(this.activeThreadKey) : this.showList();
        }
        if(this.viewMode === 'thread') {
            return this.showList();
        }
        this.closest('gudhub-messenger')?.navigateBack();
    }

}

Object.assign(GhEmailInbox.prototype, emailDataFetchMixin, emailRenderingMixin, emailComposerMixin);

if(!customElements.get('gh-email-inbox')) {
    customElements.define('gh-email-inbox', GhEmailInbox);
}
